import { useRef, useState } from "react";
import {
    motion,
    AnimatePresence,
    useScroll,
    useMotionValueEvent,
    useReducedMotion,
} from "framer-motion";
import { cn } from "@/lib/utils";

/**
 * Scroll-driven "agent run" visualisation.
 * Strong, industry-neutral B2B use case: an inbound request-for-quote with a
 * delivery deadline is classified, extracted, actioned against ERP + CRM, and
 * answered — with no human re-keying.
 * UX: a vertical stepper (left) drives a single "console" panel (right) that
 * shows one clear stage at a time. Legible on its own at any scroll point.
 */

const InBubble = ({ children }) => (
    <div className="max-w-[92%]">
        <div className="font-mono text-[11px] uppercase tracking-widest text-pa-ink-500 mb-2">
            Inbound · WhatsApp
        </div>
        <div className="bg-pa-blue-050 text-pa-ink-900 p-4 cut-tr leading-relaxed">
            {children}
        </div>
    </div>
);

const STAGES = [
    {
        key: "inbound",
        label: "Inbound",
        title: "An unstructured request arrives",
        caption: "Free text, off-hours, no form",
        render: () => (
            <InBubble>
                "Hi — we need a quote for 250 units of SKU-8842 delivered to our
                Dubai warehouse by the 30th. Please confirm price, stock, and
                lead time."
            </InBubble>
        ),
    },
    {
        key: "classify",
        label: "Classify",
        title: "Intent and priority are identified",
        caption: "One message, several jobs",
        render: () => (
            <div className="space-y-4">
                <div className="flex flex-wrap gap-2 font-mono text-xs">
                    <span className="bg-pa-blue-600 text-white px-3 py-1.5 cut-tr-sm">
                        quote_request
                    </span>
                    <span className="bg-pa-blue-050 text-pa-blue-700 px-3 py-1.5 cut-tr-sm">
                        stock_check
                    </span>
                    <span className="bg-pa-blue-050 text-pa-blue-700 px-3 py-1.5 cut-tr-sm">
                        delivery_scheduling
                    </span>
                </div>
                <div className="flex items-center gap-2 font-mono text-xs text-pa-ink-500">
                    <span className="h-2 w-2 rounded-full bg-pa-blue-600" />
                    priority: high · deadline detected
                </div>
            </div>
        ),
    },
    {
        key: "extract",
        label: "Extract",
        title: "The fields a human would retype populate themselves",
        caption: "Structured data from free text",
        render: () => (
            <dl className="font-mono text-sm divide-y divide-pa-line border border-pa-line cut-tr-sm">
                {[
                    ["sku", "SKU-8842"],
                    ["quantity", "250 units"],
                    ["destination", "Dubai warehouse"],
                    ["required_by", "30th"],
                    ["account", "matched · Acme Trading LLC"],
                ].map(([k, v]) => (
                    <div
                        key={k}
                        className="flex justify-between gap-4 px-4 py-2.5"
                    >
                        <dt className="text-pa-ink-500">{k}</dt>
                        <dd className="text-pa-ink-900 text-right">{v}</dd>
                    </div>
                ))}
            </dl>
        ),
    },
    {
        key: "act",
        label: "Act",
        title: "The agent works through your own systems",
        caption: "Read and write via API — not a demo sandbox",
        render: () => (
            <div className="space-y-2.5 font-mono text-sm">
                {[
                    ["ERP", "stock confirmed · unit price fetched"],
                    ["CRM", "account matched · quote Q-1043 created"],
                    ["Logistics", "delivery slot reserved for the 29th"],
                ].map(([sys, action]) => (
                    <div
                        key={sys}
                        className="flex items-center gap-3 bg-white border border-pa-line px-4 py-3 cut-tr-sm"
                    >
                        <span className="h-2 w-2 rounded-full bg-pa-blue-600 animate-pulse" />
                        <span className="text-pa-ink-900 w-20">{sys}</span>
                        <span className="text-pa-ink-500">{action}</span>
                    </div>
                ))}
            </div>
        ),
    },
    {
        key: "respond",
        label: "Respond",
        title: "An action completes and a grounded reply returns",
        caption: "Consistent, sourced, logged",
        render: () => (
            <div className="max-w-[92%] ml-auto">
                <div className="font-mono text-[11px] uppercase tracking-widest text-pa-blue-700 mb-2 text-right">
                    Reply · sent + logged
                </div>
                <div className="bg-pa-blue-600 text-white p-4 cut-bl leading-relaxed">
                    "Confirmed: 250 × SKU-8842 in stock. Price and lead time
                    attached — delivery by the 29th. Quote Q-1043 has been sent
                    and logged to your account."
                </div>
            </div>
        ),
    },
];

function Stepper({ active, onSelect, dark }) {
    return (
        <ol className="relative">
            <span
                className="absolute left-[19px] top-3 bottom-3 w-px bg-pa-line"
                aria-hidden="true"
            />
            <motion.span
                className="absolute left-[19px] top-3 w-px bg-pa-blue-600 origin-top"
                style={{ bottom: 12 }}
                animate={{ scaleY: (active + 1) / STAGES.length }}
                transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                aria-hidden="true"
            />
            {STAGES.map((s, i) => {
                const state =
                    i === active ? "active" : i < active ? "done" : "todo";
                return (
                    <li key={s.key} className="relative pl-14 pb-6 last:pb-0">
                        <span
                            className={cn(
                                "absolute left-0 top-0 h-10 w-10 flex items-center justify-center font-mono text-sm cut-tr-sm transition-colors duration-300",
                                state === "todo"
                                    ? "bg-white border border-pa-line text-pa-ink-500"
                                    : "bg-pa-blue-600 text-white",
                            )}
                        >
                            {String(i + 1).padStart(2, "0")}
                        </span>
                        <button
                            type="button"
                            onClick={() => onSelect(i)}
                            className="text-left block w-full"
                            data-testid={`agent-step-${i}`}
                            data-active={state === "active"}
                        >
                            <span
                                className={cn(
                                    "font-mono text-[11px] uppercase tracking-widest transition-colors",
                                    state === "active"
                                        ? "text-pa-blue-700"
                                        : "text-pa-ink-500",
                                )}
                            >
                                {s.label}
                            </span>
                            <span
                                className={cn(
                                    "block font-display text-lg font-bold leading-snug mt-0.5 transition-opacity",
                                    state === "active"
                                        ? "text-pa-ink-900 opacity-100"
                                        : "text-pa-ink-900 opacity-45",
                                )}
                            >
                                {s.title}
                            </span>
                        </button>
                    </li>
                );
            })}
        </ol>
    );
}

function Console({ active }) {
    const stage = STAGES[active];
    const reduce = useReducedMotion();
    return (
        <div className="bg-white border border-pa-line cut-tr-lg overflow-hidden shadow-[0_24px_60px_-30px_rgba(10,21,36,0.35)]">
            <div className="flex items-center justify-between px-5 py-3 border-b border-pa-line bg-pa-ink-900 text-white">
                <div className="flex items-center gap-2 font-mono text-xs">
                    <span className="h-2 w-2 rounded-full bg-pa-blue-600 animate-pulse" />
                    phoebus-agent
                </div>
                <span className="font-mono text-[11px] uppercase tracking-widest text-pa-blue-050/70">
                    step {active + 1} / {STAGES.length}
                </span>
            </div>
            <div className="p-6 md:p-8 min-h-[300px] flex flex-col justify-center">
                <AnimatePresence mode="wait">
                    <motion.div
                        key={stage.key}
                        initial={reduce ? false : { opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={reduce ? undefined : { opacity: 0, y: -12 }}
                        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                    >
                        <div className="font-mono text-[11px] uppercase tracking-widest text-pa-ink-500 mb-4">
                            {stage.caption}
                        </div>
                        {stage.render()}
                    </motion.div>
                </AnimatePresence>
            </div>
            <div className="h-1 w-full bg-pa-line">
                <motion.div
                    className="h-full bg-pa-blue-600"
                    animate={{ width: `${((active + 1) / STAGES.length) * 100}%` }}
                    transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                />
            </div>
        </div>
    );
}

export default function AgentRun() {
    const ref = useRef(null);
    const reduce = useReducedMotion();
    const [active, setActive] = useState(0);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end end"],
    });

    useMotionValueEvent(scrollYProgress, "change", (v) => {
        const i = Math.min(STAGES.length - 1, Math.floor(v * STAGES.length));
        setActive(i < 0 ? 0 : i);
    });

    // Static, fully legible fallback under reduced motion.
    if (reduce) {
        return (
            <div className="space-y-6" data-testid="agent-run-static">
                {STAGES.map((s, i) => (
                    <div
                        key={s.key}
                        className="grid md:grid-cols-[minmax(0,320px)_1fr] gap-5 items-start"
                        data-testid={`agent-stage-${i}`}
                    >
                        <div>
                            <span className="font-mono text-[11px] uppercase tracking-widest text-pa-blue-700">
                                {String(i + 1).padStart(2, "0")} · {s.label}
                            </span>
                            <h3 className="font-display text-xl font-bold leading-snug mt-1">
                                {s.title}
                            </h3>
                        </div>
                        <div className="bg-white border border-pa-line p-5 cut-tr">
                            {s.render()}
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    return (
        <div ref={ref} className="relative h-[300vh]" data-testid="agent-run">
            <div className="sticky top-[100px] grid gap-8 lg:grid-cols-[minmax(0,380px)_1fr] lg:gap-14 items-start">
                {/* Left: stepper (below console on mobile) */}
                <div className="order-2 lg:order-1">
                    <Stepper active={active} onSelect={setActive} />
                </div>
                {/* Right: live console */}
                <div className="order-1 lg:order-2">
                    <Console active={active} />
                    <p className="mt-4 font-mono text-[11px] uppercase tracking-widest text-pa-ink-500 lg:hidden text-center">
                        Scroll to run the agent
                    </p>
                </div>
            </div>
        </div>
    );
}
